import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import CourseCard from "../components/CourseCard";
import { useSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import {
  Search,
  ArrowRight,
  ShieldCheck,
  Trophy,
  TrendingUp,
  Sparkles,
  Star,
  CheckCircle2,
  Brain,
  Timer,
  Target,
  BookOpen,
  ChevronDown,
} from "lucide-react";

const TRUST_LOGOS = ["AWS", "Microsoft", "Google", "Cisco", "CompTIA", "PMI", "CNCF", "Salesforce"];

const TESTIMONIALS = [
  {
    quote: "Passed AWS SAA on the first try. The analytics told me exactly which topics to revisit.",
    name: "Priya Shankar",
    role: "Cloud Engineer · Stripe",
    rating: 5,
  },
  {
    quote: "BeforeExam felt like the actual exam. The question palette and timer made it feel real.",
    name: "Marcus Lee",
    role: "DevOps Lead · Datadog",
    rating: 5,
  },
  {
    quote: "I love the streaks. I practiced 30 days straight and crushed CKA — never been more prepared.",
    name: "Aditi Verma",
    role: "SRE · Cloudflare",
    rating: 5,
  },
];

const FAQ = [
  { q: "How is BeforeExam different from free question dumps?", a: "Every question is hand-crafted, blueprint-aligned, and explained in detail with real-exam style and difficulty calibration. Plus you get adaptive analytics that tell you exactly when you're ready." },
  { q: "Do I get a money-back guarantee?", a: "Yes — 30 days, no questions asked. If you don't pass after using BeforeExam diligently, we refund 100%." },
  { q: "How often is content updated?", a: "Every 30 days, in line with the latest exam objectives. You get all updates free for life on any course you buy." },
  { q: "Can my company buy a team license?", a: "Absolutely. Contact us for team pricing — we offer dashboards for managers and bulk discounts." },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border py-5">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
        data-testid={`faq-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
      >
        <span className="font-display text-lg pr-6">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-muted-foreground leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses/trending?limit=8").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useSEO({
    title: "Pass your next certification — practice tests for AWS, Azure, GCP & more",
    description:
      "Real-exam practice tests, adaptive analytics & a streak-powered prep engine. Trusted by 184,000+ engineers preparing for AWS, Azure, GCP, Kubernetes, DevOps, Security and more.",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "BeforeExam",
        url: typeof window !== "undefined" ? window.location.origin : "",
        logo: typeof window !== "undefined" ? `${window.location.origin}/logo192.png` : "",
        sameAs: [],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "BeforeExam",
        url: typeof window !== "undefined" ? window.location.origin : "",
        potentialAction: {
          "@type": "SearchAction",
          target: `${typeof window !== "undefined" ? window.location.origin : ""}/marketplace?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ],
  });

  return (
    <div data-testid="home-page">
      {/* HERO — dark navy island */}
      <section className="relative overflow-hidden border-b border-border crest-bg text-white" data-testid="hero-section">
        <div className="absolute inset-0 hero-mesh opacity-90" />
        <div className="aurora opacity-80" />
        <div className="absolute inset-0 grid-bg opacity-[0.30]" />
        <div className="container-x relative pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 tag-trust" data-testid="trust-badge">
                <Sparkles className="h-3.5 w-3.5" />
                Trusted by 184,000+ certification candidates worldwide
              </div>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                Pass your next certification —
                <span className="font-serif text-gradient-gold ml-1"> the day before the exam.</span>
              </h1>
              <p className="mt-5 text-lg text-white/75 max-w-2xl">
                Real-exam practice tests, adaptive analytics and a streak-powered prep engine for AWS, Azure, GCP, Kubernetes, DevOps, Security and more — built by certified engineers, not content farms.
              </p>

              <form
                onSubmit={(e) => { e.preventDefault(); navigate(`/marketplace?q=${encodeURIComponent(q)}`); }}
                className="mt-8 flex gap-2 max-w-xl"
                data-testid="hero-search"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/55" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search e.g. AWS SAA-C03, AZ-104, CKA…"
                    className="h-12 w-full rounded-md border border-white/15 bg-white/[0.07] pl-10 pr-3 text-sm text-white placeholder:text-white/45 outline-none focus:border-[color:var(--brand-amber)] focus:bg-white/[0.10] transition"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="h-12 btn-primary px-6" data-testid="hero-search-submit">
                  Find practice tests <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success-bright)]" /> 30-day money-back</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success-bright)]" /> Lifetime access</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[color:var(--brand-success-bright)]" /> Updated for 2026</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl border border-white/10 bg-white text-[color:var(--brand-navy)] p-6 shadow-2xl shadow-black/30">
                <div className="absolute -top-3 left-6 rounded-md bg-[color:var(--brand-amber)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                  Live mock score
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl">87%</span>
                  <span className="text-sm text-[color:var(--brand-text-muted)]">readiness</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                  {[{ label: "Compute", v: 92 }, { label: "Storage", v: 88 }, { label: "Networking", v: 73 }].map((t) => (
                    <div key={t.label} className="rounded-md border border-[#E8E5DC] p-3">
                      <div className="text-[color:var(--brand-text-muted)]">{t.label}</div>
                      <div className="font-display text-lg mt-1">{t.v}%</div>
                      <div className="mt-2 h-1 w-full rounded bg-[#F1ECE0]">
                        <div className="h-1 rounded bg-[color:var(--brand-amber)]" style={{ width: `${t.v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-[#E8E5DC] pt-4 text-sm">
                  <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4 text-[color:var(--brand-gold-deep)]" /> XP <b>+450</b></span>
                  <span className="flex items-center gap-1.5"><span className="text-[color:var(--brand-amber)]">🔥</span> 12-day streak</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST LOGOS */}
      <section className="border-b border-border py-10">
        <div className="container-x">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground text-center">
            Practice tests aligned with the official blueprints from
          </div>
          <div className="mt-6 grid grid-cols-4 sm:grid-cols-8 gap-6 items-center">
            {TRUST_LOGOS.map((l) => (
              <div key={l} className="text-center font-display text-lg text-muted-foreground/80 hover:text-foreground transition">
                {l}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">Categories</div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Browse by certification track</h2>
            </div>
            <Link to="/marketplace" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold hover:text-[color:var(--brand-blue)]">
              All categories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {cats.map((c) => (
              <Link
                key={c.id}
                to={`/marketplace?category=${encodeURIComponent(c.name)}`}
                className="group rounded-xl border border-border bg-card p-5 card-hover"
                data-testid={`category-card-${c.slug}`}
              >
                <div
                  className="h-10 w-10 rounded-md flex items-center justify-center font-display text-lg text-white"
                  style={{ background: c.color }}
                >
                  {c.name.charAt(0)}
                </div>
                <div className="mt-4 font-display text-base">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-1">Practice tests</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRENDING COURSES */}
      <section className="py-20 bg-[color:var(--brand-cream-2)]">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">Trending now</div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Most enrolled this week</h2>
            </div>
            <Link to="/marketplace" className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[color:var(--brand-blue)]">
              See all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.slice(0, 8).map((c) => <CourseCard key={c.id} course={c} />)}
          </div>
        </div>
      </section>

      {/* FEATURE BLOCKS */}
      <section className="py-24">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">Why BeforeExam</div>
            <h2 className="mt-2 font-display text-3xl sm:text-5xl tracking-tighter">A prep engine, not a question dump.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-12 gap-6">
            <div className="md:col-span-7 rounded-2xl border border-border p-8 bg-card relative overflow-hidden card-hover">
              <Brain className="h-10 w-10 text-[color:var(--brand-blue)]" />
              <h3 className="mt-5 font-display text-2xl">Adaptive analytics</h3>
              <p className="mt-2 text-muted-foreground max-w-md">
                Pass-probability score, weak-topic radar, speed analysis and a personalized retest plan after every attempt.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[{ l: "Pass probability", v: "92%" }, { l: "Weak topic", v: "VPC" }, { l: "Speed", v: "1.4×" }].map((s) => (
                  <div key={s.l} className="rounded-lg border border-border p-3">
                    <div className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground">{s.l}</div>
                    <div className="font-display text-xl mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 rounded-2xl border border-border p-8 bg-card">
              <Timer className="h-10 w-10 text-[color:var(--brand-flame)]" />
              <h3 className="mt-5 font-display text-2xl">Real exam simulator</h3>
              <p className="mt-2 text-muted-foreground">
                Timed mode, full-screen focus, mark-for-review, question palette — every detail, just like the real test.
              </p>
            </div>
            <div className="md:col-span-5 rounded-2xl border border-border p-8 bg-card">
              <Target className="h-10 w-10 text-[color:var(--brand-success)]" />
              <h3 className="mt-5 font-display text-2xl">Streaks & XP</h3>
              <p className="mt-2 text-muted-foreground">
                Daily streaks, XP, badges and leaderboards — the proven formula that keeps you practicing.
              </p>
            </div>
            <div className="md:col-span-7 rounded-2xl border border-border p-8 bg-card">
              <BookOpen className="h-10 w-10 text-[color:var(--brand-blue)]" />
              <h3 className="mt-5 font-display text-2xl">Detailed explanations on every question</h3>
              <p className="mt-2 text-muted-foreground max-w-lg">
                Stop memorizing. Understand the why with curated explanations, official documentation links, and topic context for every question.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 border-y border-border bg-[color:var(--brand-cream-2)]">
        <div className="container-x">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">Success stories</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">From practice to pass</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-xl border border-border p-6 bg-card">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-yellow-500 stroke-yellow-500" />
                  ))}
                </div>
                <p className="mt-4 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 text-sm">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-muted-foreground">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + FAQ */}
      <section className="py-24">
        <div className="container-x grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">FAQ</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Questions, answered.</h2>
            <div className="mt-8">
              {FAQ.map((f) => <FaqItem key={f.q} {...f} />)}
            </div>
          </div>
          <div className="rounded-2xl border border-border p-10 crest-bg text-white relative overflow-hidden flex flex-col justify-between">
            <div className="absolute inset-0 grid-bg opacity-10" />
            <div className="aurora opacity-50" />
            <div className="relative">
              <ShieldCheck className="h-10 w-10 text-[color:var(--brand-gold)]" />
              <h3 className="mt-6 font-display text-3xl tracking-tight text-white">
                Practice today. Pass <span className="font-serif text-gradient-gold">tomorrow.</span> Get refunded if you don't.
              </h3>
              <p className="mt-3 text-white/70 max-w-md">
                Join 184,000+ students. 30-day money-back guarantee. Lifetime access. Free updates.
              </p>
            </div>
            <div className="relative mt-8 flex flex-wrap gap-3">
              <Link to="/marketplace">
                <Button className="h-12 btn-primary px-6" data-testid="cta-browse">
                  Browse practice tests <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="h-12 border-white/30 bg-white/0 hover:bg-white/10 text-white px-6" data-testid="cta-pricing">
                  See plans
                </Button>
              </Link>
            </div>
            <div className="relative mt-8 grid grid-cols-3 gap-2 text-xs">
              <div><div className="font-display text-2xl">184k+</div><div className="text-white/60">Students</div></div>
              <div><div className="font-display text-2xl">94%</div><div className="text-white/60">Pass rate</div></div>
              <div><div className="font-display text-2xl">4.8★</div><div className="text-white/60">Rating</div></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
